import {ColorUtils} from "./colorUtils";
import type {
    ChromoLogoConstructorArguments,
    LogoConstructorArguments,
    SelObConstructorArguments,
    StockLogoConstructorArguments
} from "./selObInterfaces";
import {FontHandler} from "./fontHandler";
import {EventVerteiler} from "../global/eventVerteiler";
import {createGUID, intToBool, padLeftToLength} from "../global/globalUtils";
import {CONSTANTS} from "./meta_data";
import {Vector2} from "./delme_globals";
import globalsObject from "./globalsObject";
import DEBUG from "./DEBUG";
import axios from "axios";
import {deepCopy} from "../global/deepCopy";
import {SelObTypes} from "./SelObTypes";
import {TextObjectInhalt} from "./textObjectInhalt";

export enum SelObFloatingStates {TOP, FREE, BOTTOM}

export class SelectableObject {
    id : string;
    rotation : number;
    translation : Vector2;
    relSize : Vector2;
    type : string;
    partName : string;
    cloneIds : string[];
    cloningAngleRad;
    zOrder : number;
    repeat : boolean;
    clampToColor : number;
    wasserzeichen : boolean;
    wasserzeichenInvertImage : boolean;
    wasserzeichenIntensity : number;
    isPinned : boolean;
    floating : SelObFloatingStates;
    keepSeparate : boolean;
    allowDrag : boolean;
    allowDelete : boolean;
    allowResize : boolean;
    isVorbelegung : boolean;
    isClone =false;
    isPattern  = false;
    isAILogo =false;
    ursprungsMotivID:number;
    hasTackleTwill : boolean;
    berechnungsFeature : string;
    dpi : number;

    constructor(parameters : SelObConstructorArguments) {
        this.id = parameters.id || createGUID();
        this.rotation = parameters.rotation || 0;
        this.translation = parameters.translation || new Vector2(0, 0);
        this.relSize = parameters.relSize || new Vector2(NaN, NaN);
        this.type = "bogusType";
        this.partName = parameters.partName || "";
        this.cloneIds = parameters.cloneIds || [];

        /**
         * @member {object}
         */
        this.cloningAngleRad = {};
        this.zOrder = parameters.zOrder || 0;
        this.repeat = parameters.repeat || false;
        /*
         * Soll die textur nur auf einer Farbe angezeigt werden?
         * -1 : Zeige die Textur auf allen ColorCodes an.
         *  0 : Zeige die Textur nur auf dem ColorCode A an.
         *  1 : Zeige die Textur nur auf dem ColorCode B an.
         *  []...]
         */
        this.clampToColor = parameters.clampToColor || -1;
        /*
         * Soll die Textur als Wasserzeichen verwendet werden?
         * Hierbei würd der Helligkeitswert der Textur genommen, um die darunter liegenden Farben abzudunkeln,
         * oder aufzuhellen.
         */
        this.wasserzeichen = parameters.wasserzeichen || false;

        /*
         * Standardmässig wird alles wo schwarz ist auf dem Bild die Textur aufgehellt oder abgedunkelt.
         * Man kann hiermit die Textur invertieren und damit sagen, dass die weissen flächen der Textur verwendet werden
         * sollen um die Farbe abzudunkeln oder aufzuhellen.
         */
        this.wasserzeichenInvertImage = parameters.wasserzeichenInvertImage || false;

        /**
         * Wie stark soll die Farbe durch das Wasserzeichen abgedunkelt bzw. aufgehellt werden.
         * -1 wäre schwarz +1 doppelt so hell.
         */
        this.wasserzeichenIntensity = parameters.wasserzeichenIntensity || -0.2;
        this.isPinned = false;
        this.floating = parameters.floating || CONSTANTS.FLOAT_FREE;
        this.keepSeparate = parameters.keepSeparate || false;
        this.allowDrag = parameters.allowDrag !== false;
        this.allowDelete = parameters.allowDelete !== false;
        this.allowResize = parameters.allowResize !== false;
        this.isVorbelegung = parameters.isVorbelegung || false;
        this.ursprungsMotivID = parameters.ursprungsMotivID || null;
        this.hasTackleTwill = parameters.hasTackleTwill || false;
        this.berechnungsFeature = parameters.berechnungsFeature || null;
    }

    translationIsInBounds(){
        return this.translation.x >= 0 && this.translation.y >= 0 && this.translation.x <= 1 && this.translation.y <= 1;
    }

    makeUntouchable(floatingWert = CONSTANTS.FLOAT_TOP) {
        this.allowDrag = false;
        this.allowDelete = false;
        this.allowResize = false;
        this.floating = floatingWert;
        this.keepSeparate = true;
    }
    makeEditable(floatingWert = CONSTANTS.FLOAT_FREE) {
        this.allowDrag = true;
        this.allowDelete = true;
        this.allowResize = true;
        this.floating = floatingWert;
        this.keepSeparate = false;
    }

    setPatternProperties(clampToColor = -1) {
        this.allowDrag = false;
        this.allowDelete = false;
        this.allowResize = false;
        this.floating = CONSTANTS.FLOAT_BOTTOM;
        this.clampToColor = clampToColor;
    }


    clone(keepIdAndCloneIds = false) {
        let output;

        try{
            output = SelectableObject.createSelObFromType(this.type);
        }
        catch (e) {
            if (DEBUG) {
                alert(e);
            }
            return null;
        }

        for (const property in this) {
            output = this.clonePropertyToObject(property, output, keepIdAndCloneIds);
        }
        return output;
    }

    clonePropertyToObject(property, output, keepIdAndCloneIds) {
        if (property === "id") {
            if (keepIdAndCloneIds === true) {
                output[property] = this[property];
            } else {
                output[property] = createGUID();
            }
            return output;
        }
        if (property === "cloneIds") {
            if (keepIdAndCloneIds === true) {
                output[property] = deepCopy([],  this[property]);
            } else {
                output[property] = [];
            }
            return output;
        }
        if (typeof (this[property]) === "object") {
            if (Array.isArray(this[property])) {
                output[property] = deepCopy(true, [], this[property]);
            } else if (this[property] === null) {
                output[property] = null;
            } else {
                output[property] = deepCopy(true, {}, this[property]);
            }
        } else {
            output[property] = this[property];
        }
        return output;
    }

    public static createSelObFromType(type):SelectableObject {
        switch (type) {
            case  SelObTypes.TEXT:
                return Object.create(TextObject.prototype);
            case SelObTypes.LOGO:
                return Object.create(Logo.prototype);
            case SelObTypes.CHROMOLOGO:
                return Object.create(ChromoLogo.prototype);
            case SelObTypes.STOCKLOGO:
                return Object.create(StockLogo.prototype);
            default:
                throw "illegal SelObType";
        }
    }
}


export class Logo extends SelectableObject {
    filename : string;
    pathToLogo : string;
    logoId : string;
    zuLoeschenBeiProduktUndDesignWechsel:boolean

    constructor(parameters : LogoConstructorArguments) {
        super(parameters);
        this.filename = parameters.filename || "";
        this.pathToLogo = parameters.pathToLogo as string;
        this.type = SelObTypes.LOGO;
        this.logoId = parameters.logoId as string;
        this.isAILogo = parameters.isAILogo || false;
        this.zuLoeschenBeiProduktUndDesignWechsel = typeof(parameters.zuLoeschenBeiProduktUndDesignWechsel) === "undefined" ? false : intToBool(parameters.zuLoeschenBeiProduktUndDesignWechsel)
    }
}


export class ChromoLogoColor {
    colorIntValue;
    displayName;
    vorbelegung;
    colorCode;
    palette;
    palettenCluster = "CM5 Bedruckbar";
    farbnummerNachFarbanteil = -1;
    kopplungAnColorCode:string|null
    kopplungAufgehoben = false

    public static clone(toClone:ChromoLogoColor):ChromoLogoColor {
        const out = new ChromoLogoColor();
        out.colorIntValue = toClone.colorIntValue;
        out.displayName = toClone.displayName;
        out.vorbelegung = toClone.vorbelegung;
        out.colorCode = toClone.colorCode;
        out.palette = toClone.palette;
        out.palettenCluster = toClone.palettenCluster;
        out.farbnummerNachFarbanteil = toClone.farbnummerNachFarbanteil;
        out.kopplungAnColorCode = toClone.kopplungAnColorCode;
        out.kopplungAufgehoben = toClone.kopplungAufgehoben;
        return out;
    }

}
export interface TextObjectConstructorArguments extends SelObConstructorArguments{

    DBRotatedRelSize?:Vector2;
    isFixed?:boolean;
    outlineColorValue?:any;
    outlineColorValueQuellpalette?:string;
    outlineZweiColor?:number|string;
    outlineZweiColorPalette?:string|null;
    outlineLineJoin?:string;
    value?:string;
    font?:string;
    colorValue?: number;
    colorValueQuellpalette? : string
    outlineWidth?: number;
    relSize?: Vector2;
    characterSpacingProzent?:number;
}

export class TextObject extends SelectableObject implements TextObjectInhalt{
    font : string;
    value : string;
    colorValue : number;
    colorQuellPalette :string;
    outlineColorValue : number|string;
    outlineColorValueQuellpalette : string;
    outlineWidth : number;
    outlineLineJoin : string;
    outlineWidthLevel  = NaN;
    outlineZweiColor : string|number;
    outlineZweiColorPalette : string|null;
    characterSpacingProzent:number;
    isFixed : boolean;
    DBRotatedRelSize : Vector2;

    constructor(parameters : TextObjectConstructorArguments) {
        super(parameters);
        this.font = parameters.font || CONSTANTS.TEXTOBJECT_DEFAULTFONT;
        this.value = parameters.value || "";
        this.colorValue = parameters.colorValue || CONSTANTS.DEFAULT_TEXT_COLOR;
        this.colorQuellPalette = parameters.colorValueQuellpalette || CONSTANTS.STANDARD_PALETTE;
        this.outlineColorValue = typeof(parameters.outlineColorValue) !== "undefined" ? parameters.outlineColorValue : "<none>";
        this.outlineColorValueQuellpalette = parameters.outlineColorValueQuellpalette || CONSTANTS.STANDARD_PALETTE;
        this.outlineWidth = parameters.outlineWidth || 0;
        this.outlineLineJoin = parameters.outlineLineJoin || FontHandler.getOutlineLineJoin(this.font);
        this.outlineZweiColor = parameters.outlineZweiColor || "<none>";
        this.outlineZweiColorPalette = parameters.outlineZweiColorPalette || null;
        this.characterSpacingProzent = parameters.characterSpacingProzent || 0;
        this.isFixed = parameters.isFixed || false;

        this.type = SelObTypes.TEXT;

        this.relSize = parameters.relSize || new Vector2(NaN, NaN);

        this.DBRotatedRelSize = parameters.DBRotatedRelSize || new Vector2(NaN, NaN);
    }

    getValue(): string {
        return this.value;
    }
    getID(): string {
        return this.id;
    }
    getIsFixed(): boolean {
        return this.isFixed;
    };

    adoptColorsAndFontFromOtherTextObject(otherTextObject:TextObject) {
        this.colorValue = otherTextObject.colorValue;
        this.font = otherTextObject.font;
        this.outlineColorValue = otherTextObject.outlineColorValue;
        this.outlineWidth = otherTextObject.outlineWidth;
        this.outlineLineJoin = otherTextObject.outlineLineJoin;
    }

    public getHexColorValue(){
        return ColorUtils.decToHex(this.colorValue);
    }
    public getHexOutlineColorValue() {
        return ColorUtils.decToHex(this.outlineColorValue as number);
    }
    public static getFontSize(textInhalt:string) {
        return textInhalt.length < 3 ? CONSTANTS.TEXTOBJECT_FONTBASESIZE * 5 : CONSTANTS.TEXTOBJECT_FONTBASESIZE;
    }
}


export class ChromoLogo extends Logo {
    originalSVG : string;
    allowColorChange : boolean;
    colors : ChromoLogoColor[];
    currentSVG : string;
    chromoLogoId : string;
    showColorsInLogoTab =true;
    showColorsInColorTab =false;
    farbkombinationen : any;

    constructor(parameters : ChromoLogoConstructorArguments) {
        super(parameters as LogoConstructorArguments);
        this.type = SelObTypes.CHROMOLOGO;
        this.originalSVG = parameters.originalSVG || ""; /* Das SVG mit Platzhaltern */
        this.currentSVG = ""; /* hier wird das SVG mit ersetzten Platzhaltern gespeichert */
        this.allowColorChange = parameters.allowColorChange || true;
        this.colors = parameters.colors || [];
        this.chromoLogoId = parameters.chromoLogoId as string;
        this.isPattern = parameters.isPattern || false;
    }

    /**
     * Lädt den SVG Text mit Platzhaltern vom Server mit Zusatzdaten aus der Datenbank
     * @param {boolean} [initializeFromVorbelegung=false]
     */
    getOriginalSVG(initializeFromVorbelegung) {
        globalsObject.resourceManager.executeInvalidation("loadingSVG"+this.id);
        const $this = this;
        axios.get("/konfigurator_php/actionmodul/getChromoLogo.php?id=" + this.chromoLogoId)
            .then(response=> $this.initFromOriginalSVG(response.data, initializeFromVorbelegung))
            .catch(()=>console.log("could not load svg"))
    }

    protected initFromOriginalSVG(data, initializeFromVorbelegung) {
        const $this = this;
        $this.originalSVG = data.svg;
        $this.showColorsInLogoTab = true;//data.ShowColorsInLogoTab;
        $this.showColorsInColorTab = false;//data.ShowColorsInColorTab;
        $this.farbkombinationen = data.farbkombinationen;

        if (initializeFromVorbelegung) {
            $this.colors = data.colorCodes;
            $this.setColorValuesFromVorbelegung();
        }
        const anzColors = $this.colors.length;
        for (let i = 0; i < anzColors; i++) {
            $this.colors[i].displayName = data.colorCodes[i].displayName;
        }

        $this.createCurrentSVG();
        globalsObject.resourceManager.registerResource({resourceName:"chromoLogoSVG"+$this.id, startingStati:["loaded"], invalidatedWhen:"loadingSVG"+this.id});


        EventVerteiler.broadcastBlockRequestEnded("loading_" + $this.id);

        // if ($this.showColorsInColorTab === true && $this.allowColorChange) {
        //     EventVerteiler.broadcast("initLogoColorPickerEvent", $this);
        // }
    }

    private setColorValuesFromVorbelegung() {
        this.colors = this.colors.map(function(color) {
            color.colorIntValue = color.vorbelegung;
            return color;
        });
    }

    createCurrentSVG() {
        this.currentSVG = this.colors.reduce(
            function(svgText, color) {
                let regex = new RegExp("\\[\\[" + color.colorCode + "\\]\\]", "gim");
                if(svgText.match(regex) === null) {
                    regex = new RegExp(color.colorCode, "gim");
                }
                svgText = svgText.replace(regex, "#" + ColorUtils.decToHex(color.colorIntValue));
                return svgText;
            },
            this.originalSVG
        );
        globalsObject.resourceManager.registerResource({resourceName:"loading_"+this.id, startingStati:["finished"]}).invalidAfter("changing_"+this.id);
    }
}


export class StockLogo extends ChromoLogo {
    public stockLogoID: string;
    public previewPicture:string;
    public standardSizeCM;
    public maxSizeCM;
    public minSizeCM;
    public targetSizeCM;
    public patternAnkerFromDB:Vector2|null;

    public constructor(params:StockLogoConstructorArguments) {
        super(params as ChromoLogoConstructorArguments);
        this.type = SelObTypes.STOCKLOGO;
        this.stockLogoID = params.stockLogoID;
        this.previewPicture = StockLogo.getPreviewPictureURL(this.stockLogoID);
        this.patternAnkerFromDB = params.patternAnkerFromDB||null;
    }

    public setSizes(standard, min, max) {
        this.standardSizeCM = parseFloat(standard);
        this.maxSizeCM = parseFloat(max);
        this.minSizeCM = parseFloat(min);
    }

    public static getPreviewPictureURL(stockLogoID) {
        return ["", "logodatenbank", padLeftToLength((parseInt(stockLogoID, 10) % 100).toString(), 2, "0"), stockLogoID+"_300.png"].join("/");
    }

    getOriginalSVG(initializeFromVorbelegung) {
        globalsObject.resourceManager.executeInvalidation("loadingSVG"+this.id);
        globalsObject.stockLogoStore.getOriginalSVG(this.stockLogoID).then(data=>this.initFromOriginalSVG(data, initializeFromVorbelegung));
    }

    public getPrimaryColor() {
        return this.colors.reduce(function(carry:ChromoLogoColor|null, current:ChromoLogoColor) {
            if(carry === null) {
                return current;
            }
            return carry.farbnummerNachFarbanteil < current.farbnummerNachFarbanteil ? carry : current;
        }, null);
    }
}


export class StockLogoFromBackend {
    public Filename;
    public AnzColors;
    public ShowColorsInColorTab;
    public ShowColorsInLogoTab;
    public Palette;
    public colorCodes = [];
    public farbkombinationen = [];
    public svg;
}
